'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import useRegisterStore from '@/stores/register';
import useRegisterPageFactory from '@/components/RegisterPage/Factory';
import { ArrowLeft, ShieldCheck, RefreshCw } from 'lucide-react';
import { useCallback, useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import type { RegisterFormData } from '@/stores/register/types';
import { Spinner } from '@/components/ui/spinner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import useRudderStackAnalytics from '@/hooks/useRudderAnalytics';
import useIsWebview from '@/hooks/use-is-webview';
import { useSearchParams } from 'next/navigation';
import { getRandomAvatar } from '@/const';
import RegisterFormSchema from '@/components/RegisterPage/schama';
import ErrorMessage from '@/components/RegisterPage/ErrorMessage';
const RegisterPersonalDataStep = () => {
  const $ = _c(73);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["previousStep", "nextStep", "handleChangeDocument", "handleChangeDate"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    previousStep,
    handleChangeDocument,
    handleChangeDate
  } = useRegisterStore(t0);
  const [isWebView] = useIsWebview();
  const searchParams = useSearchParams();
  let t1;
  if ($[1] !== searchParams) {
    t1 = searchParams.get("skipPassword");
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const skipPassword = t1 === "true";
  const {
    isSendRegisterForm,
    handleSubmit
  } = useRegisterPageFactory();
  const form = useFormContext();
  const analytics = useRudderStackAnalytics();
  const hasTrackedPageView = useRef(false);
  const [selectedAvatar, setSelectedAvatar] = useState("");
  const [isRotating, setIsRotating] = useState(false);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [errors, setErrors] = useState(t2);
  let t3;
  let t4;
  if ($[4] !== errors || $[5] !== form) {
    t3 = () => {
      const subscription = form.watch((_values, t5) => {
        const {
          name
        } = t5;
        if (name && errors[name]) {
          setErrors(prev => {
            const next = {
              ...prev
            };
            delete next[name];
            return next;
          });
        }
      });
      return () => subscription.unsubscribe();
    };
    t4 = [form, errors];
    $[4] = errors;
    $[5] = form;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[8] !== form) {
    t5 = () => {
      const randomAvatar = getRandomAvatar();
      setSelectedAvatar(randomAvatar);
      form.setValue("avatarImage", randomAvatar);
    };
    t6 = [form];
    $[8] = form;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  if ($[11] !== form || $[12] !== handleSubmit || $[13] !== isWebView || $[14] !== skipPassword) {
    t7 = async () => {
      const values = form.getValues();
      const schema = RegisterFormSchema(isWebView, skipPassword);
      const result = schema.safeParse(values);
      if (!result.success) {
        const fieldErrors = {};
        for (const issue of result.error.issues) {
          const field = issue.path[0] as string;
          if (["name", "dateOfBirth", "document"].includes(field) && !fieldErrors[field]) {
            fieldErrors[field] = issue.message;
          }
        }
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          return;
        }
      }
      setErrors({});
      await handleSubmit(values);
    };
    $[11] = form;
    $[12] = handleSubmit;
    $[13] = isWebView;
    $[14] = skipPassword;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const onSubmit = t7;
  let t8;
  if ($[16] !== form || $[17] !== selectedAvatar) {
    t8 = () => {
      setIsRotating(true);
      const newAvatar = getRandomAvatar(selectedAvatar);
      setSelectedAvatar(newAvatar);
      form.setValue("avatarImage", newAvatar);
      setTimeout(() => setIsRotating(false), 500);
    };
    $[16] = form;
    $[17] = selectedAvatar;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleRandomizeAvatar = t8;
  let t10;
  let t9;
  if ($[19] !== analytics || $[20] !== isWebView) {
    t9 = () => {
      if (!analytics || hasTrackedPageView.current) {
        return;
      }
      hasTrackedPageView.current = true;
      analytics.track("Register Step", {
        step: "personal_data",
        platform: isWebView ? "app" : "web"
      });
    };
    t10 = [analytics, isWebView];
    $[19] = analytics;
    $[20] = isWebView;
    $[21] = t10;
    $[22] = t9;
  } else {
    t10 = $[21];
    t9 = $[22];
  }
  useEffect(t9, t10);
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ArrowLeft className="size-5" />;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== previousStep) {
    t12 = <Button type="button" variant="none" size="iconArea" onClick={previousStep} className="text-primary md:hidden hover:text-primary/80 transition-colors self-start">{t11}</Button>;
    $[24] = previousStep;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = `/images/avatars/${selectedAvatar}`;
  let t14;
  if ($[26] !== t13) {
    t14 = <AvatarImage src={t13} alt="Avatar" />;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <AvatarFallback className="bg-gradient-to-br from-gray-700 to-gray-800"><Spinner size="lg" spinnerClassName="bg-white" /></AvatarFallback>;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t14) {
    t16 = <Avatar className="size-32 border-4 border-white/10 group-hover:border-white/20 transition-all group-hover:scale-105 group-active:scale-95">{t14}{t15}</Avatar>;
    $[29] = t14;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = `size-4 text-white dark:text-black transition-transform duration-500 ${isRotating ? "rotate-180" : ""}`;
  let t18;
  if ($[31] !== t17) {
    t18 = <div className="absolute bottom-0 right-0 bg-primary rounded-full p-2 group-hover:scale-110 transition-all group-active:scale-95 pointer-events-none"><RefreshCw className={t17} /></div>;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== handleRandomizeAvatar || $[34] !== isRotating || $[35] !== t16 || $[36] !== t18) {
    t19 = <div className="flex flex-col items-center gap-6 pt-4 md:p-6"><button type="button" onClick={handleRandomizeAvatar} disabled={isRotating} className="relative group cursor-pointer disabled:cursor-not-allowed" aria-label="Clique para trocar o avatar">{t16}{t18}</button></div>;
    $[33] = handleRandomizeAvatar;
    $[34] = isRotating;
    $[35] = t16;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== errors.name) {
    t20 = t21 => {
      const {
        field: field_0
      } = t21;
      return <FormItem><FormControl><Input {...field_0} type="text" inputSize="xl" rounded="2xl" variant="outline" autoComplete="name" floatingLabel={true} label="Nome completo" /></FormControl><ErrorMessage message={errors.name} /></FormItem>;
    };
    $[38] = errors.name;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== form.control || $[41] !== t20) {
    t21 = <FormField control={form.control} name="name" render={t20} />;
    $[40] = form.control;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== errors.dateOfBirth || $[44] !== handleChangeDate) {
    t22 = t23 => {
      const {
        field: field_1
      } = t23;
      return <FormItem><FormControl><Input {...field_1} type="text" inputSize="xl" rounded="2xl" variant="outline" inputMode="numeric" maxLength={10} onChange={handleChangeDate} floatingLabel={true} label="Data de nascimento" /></FormControl><ErrorMessage message={errors.dateOfBirth} /></FormItem>;
    };
    $[43] = errors.dateOfBirth;
    $[44] = handleChangeDate;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== form.control || $[47] !== t22) {
    t23 = <FormField control={form.control} name="dateOfBirth" render={t22} />;
    $[46] = form.control;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== errors.document || $[50] !== handleChangeDocument) {
    t24 = t25 => {
      const {
        field: field_2
      } = t25;
      return <FormItem><FormControl><Input {...field_2} type="text" inputSize="xl" rounded="2xl" variant="outline" inputMode="numeric" maxLength={14} onChange={handleChangeDocument} floatingLabel={true} label="CPF" /></FormControl><ErrorMessage message={errors.document} /></FormItem>;
    };
    $[49] = errors.document;
    $[50] = handleChangeDocument;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== form.control || $[53] !== t24) {
    t25 = <FormField control={form.control} name="document" render={t24} />;
    $[52] = form.control;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t21 || $[56] !== t23 || $[57] !== t25) {
    t26 = <div className="flex flex-col gap-6">{t21}{t23}{t25}</div>;
    $[55] = t21;
    $[56] = t23;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t12 || $[60] !== t19 || $[61] !== t26) {
    t27 = <div className="flex flex-col gap-6">{t12}{t19}{t26}</div>;
    $[59] = t12;
    $[60] = t19;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="flex justify-center items-center gap-2"><ShieldCheck size={20} className="text-green-600" /><span className="text-muted-foreground text-xs">Dados seguros pela LGPD com criptografia</span></div>;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== isSendRegisterForm) {
    t29 = isSendRegisterForm ? <div className="flex items-center gap-2"><Spinner size="lg" spinnerClassName="bg-white dark:bg-black" /><span>Criando conta...</span></div> : "Criar conta";
    $[64] = isSendRegisterForm;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== isSendRegisterForm || $[67] !== onSubmit || $[68] !== t29) {
    t30 = <div className="flex flex-col gap-4">{t28}<Button type="button" onClick={onSubmit} disabled={isSendRegisterForm} size="xl" rounded="full" className="md:max-w-[240px] md:mx-auto  font-semibold w-full">{t29}</Button></div>;
    $[66] = isSendRegisterForm;
    $[67] = onSubmit;
    $[68] = t29;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== t27 || $[71] !== t30) {
    t31 = <div className="min-h-screen w-full flex flex-col px-4 py-8 md:max-w-lg md:mx-auto md:py-20"><div className="flex-1 flex flex-col justify-between md:justify-start gap-4 md:gap-10 w-full">{t27}{t30}</div></div>;
    $[70] = t27;
    $[71] = t30;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  return t31;
};
export default RegisterPersonalDataStep;